import React, {useCallback, useState} from 'react'
import {RouteComponentProps, navigate} from '@reach/router'
import {Formik, FormikConfig, FormikProps} from 'formik'
import {CartCheck} from 'react-bootstrap-icons'
import {
  startOfTomorrow,
  add as dateAdd,
  parseISO as dateParseISO,
  isWeekend,
} from 'date-fns'

import {useApi} from '../../api/rest'
import {useAuth} from '../../contexts/UserContext'
import {useCart} from '../../contexts/CartContext'
import RadioField from '../form/fields/RadioField'
import DatePickerField, {DatePickerFieldDatePicker} from '../form/fields/DatePickerField'

interface FormValues {
  address: string
  date: string
}

interface FormErrors {
  address?: string
  date?: string
}

const reqMsg = 'Þennan reit þarf að fylla út'


function validate({
  address,
  date,
}: FormValues) {
  const obj: FormErrors = {}

  if (!address) {
    obj.address = reqMsg
  }

  if (!date) {
    obj.date = reqMsg
  }

  return obj
}

export default function CheckoutForm(_: RouteComponentProps) {
  const {company} = useAuth()
  const {completeCheckout, cartUpdateMeta} = useCart()

  const [error, setError] = useState<String | null>()

  const onSubmit = useCallback(async (values: FormValues) => {
    setError(undefined)

    await cartUpdateMeta({date: values.date})

    const res = await completeCheckout()
    if (!res) {
      setError('Eitthvað fór úrskeiðis')
      return
    }

    if (res.checkoutErrors?.length) {
      // TODO: Connect errors to fields
      setError(res.checkoutErrors[0].message)
      return
    }

    navigate(`/verslun/pantanir/${res.order?.token}/`)
  }, [completeCheckout, cartUpdateMeta])

  const formikProps: FormikConfig<FormValues> = {
    initialValues: {
      address: `${company?.default_shipping_address || ''}`,
      date: '',
    },
    onSubmit,
    validate,
    enableReinitialize: true,
  }

  return (
    <>
      {!!error && (
        <div className="alert alert-danger px-5 py-4 mb-3" role="alert">
          <h4 className="alert-heading">Eitthvað fór úrskeiðis!</h4>
          <p className="mb-0">{error}</p>
        </div>
      )}
      <Formik component={Form} {...formikProps} />
    </>
  )
}

interface Holiday {
  date: string
  name: string
}

function Form({
  handleSubmit,
  isValid,
  isSubmitting,
  dirty,
  isInitialValid,
}: FormikProps<FormValues>) {
  const {company} = useAuth()
  const {shippingPrice} = useCart()
  const {
    data: holidays,
    isValidating: holidaysLoading,
  } = useApi<Holiday[]>('/holidays/')

  const minDate = startOfTomorrow()
  const maxDate = dateAdd(minDate, {days: 14})

  const datePickerProps: DatePickerFieldDatePicker = {
    minDate,
    maxDate,
    disabled: holidaysLoading,
    excludeDates: holidays?.map(({date}) => dateParseISO(date)),
    filterDate: (d) => !isWeekend(d),
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="row mb-5">
        <div className="col-12">
          <div className="p-5 bg-white rounded border border-gray">
            <h5 className="mb-4">Hvert á að senda vörurnar?</h5>
            <div className="row">
              {company?.addresses.map((address, i) => (
                <RadioField
                  key={i}
                  className="col-12 col-lg-6 mb-3"
                  name="address"
                  value={`${address.id}`}
                  label={address.name || address.street_address_1}
                  description={(
                    <>
                      <div>{address.street_address_1}</div>
                      <div>{address.postal_code} {address.city}</div>
                    </>
                  )}
                />
              ))}
            </div>

            <h5 className="mb-4">Hvenær á að senda vörurnar?</h5>
            <DatePickerField name="date" inline datePicker={datePickerProps} />
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-end w-100">
        <button
          className="btn btn-lg btn-primary d-flex align-items-center"
          type="submit"
          disabled={!isValid || (!dirty && !isInitialValid) || isSubmitting || shippingPrice == null}
        >
          {isSubmitting && (
            <div className="spinner-border spinner-border-sm text-white mr-3" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          )}

          {!isSubmitting ? 'Ganga frá kaupum' : 'Geng frá kaupum'}

          <CartCheck size={20} className="text-dark ml-2" />
        </button>
      </div>
    </form>
  )
}
